import { isDatadogInitialized, logToDatadog } from '../datadog/datadog';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  userId?: string;
  orderId?: string;
  component?: string;
  action?: string;
  [key: string]: unknown;
}

interface LogEntry {
  ts: string; // ISO8601 timestamp
  level: LogLevel;
  msg: string; // message
  service: string; // service name
  env: string; // environment
  request_id?: string;
  trace_id?: string;
  span_id?: string;
  [key: string]: unknown; // additional context
}

// Global function to get trace context from OpenTelemetry (will be set by browser-otel.ts)
let getTraceContext: (() => { traceId?: string; spanId?: string }) | null = null;

/**
 * Set the trace context getter function (called by browser-otel.ts during initialization)
 */
export function setTraceContextGetter(getter: () => { traceId?: string; spanId?: string }): void {
  getTraceContext = getter;
}

class Logger {
  private serviceName: string;
  private environment: string;
  private minLevel: LogLevel;
  private redactionPaths: string[];
  private requestId: string;

  constructor() {
    this.serviceName = 'tangi-frontend';
    this.environment = this.detectEnvironment();
    this.minLevel = this.getMinLevel();
    this.redactionPaths = this.getRedactionPaths();
    this.requestId = this.generateRequestId();
  }

  private detectEnvironment(): string {
    // Production: Vite production build
    if (import.meta.env.MODE === 'production') {
      // Check if running on production domain
      if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        if (hostname === 'tangi.ai' || hostname === 'www.tangi.ai') {
          return 'production';
        }
        // Production build but not on production domain = staging
        return 'staging';
      }
      return 'production';
    }

    // Localhost: Running on localhost
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'localhost';
      }
    }

    // Default: development
    return 'development';
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getMinLevel(): LogLevel {
    const envLevel = (import.meta.env['VITE_LOG_LEVEL'] as string | undefined)?.toLowerCase() ||
      (this.environment === 'production' ? 'info' : 'debug');

    if (typeof envLevel === 'string' && ['debug', 'info', 'warn', 'error'].includes(envLevel)) {
      return envLevel as LogLevel;
    }
    return 'info';
  }

  private getRedactionPaths(): string[] {
    const envPaths = import.meta.env['VITE_LOG_REDACTION_PATHS'] as string | undefined;
    if (envPaths && typeof envPaths === 'string') {
      return envPaths.split(',').map((p: string) => p.trim());
    }
    return [
      'password',
      'token',
      'apiKey',
      'secret',
      'authorization',
      'card.*',
      'email',
    ];
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    return levels.indexOf(level) >= levels.indexOf(this.minLevel);
  }

  private redactSensitiveData(obj: Record<string, unknown>, path = ''): Record<string, unknown> {
    const redacted = { ...obj };

    for (const redactionPath of this.redactionPaths) {
      const pathParts = redactionPath.split('.');
      let current: Record<string, unknown> | null | undefined = redacted;

      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i];
        if (!part) continue;
        if (part === '*') {
          // Wildcard: redact all keys at this level
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- typeof null === 'object' in JavaScript
          if (current && typeof current === 'object' && current !== null) {
            const currentObj = current;
            for (const key in currentObj) {
              if (pathParts[i + 1]) {
                // Continue to next level
                if (typeof currentObj[key] === 'object' && currentObj[key] !== null) {
                  currentObj[key] = this.redactSensitiveData(currentObj[key] as Record<string, unknown>, `${path}.${key}`);
                }
              } else {
                // This is the final level, redact
                currentObj[key] = '[REDACTED]';
              }
            }
          }
          break;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- typeof null === 'object' in JavaScript
        } else if (current && typeof current === 'object' && current !== null && part in current) {
          current = (current)[part] as Record<string, unknown> | null | undefined;
        } else {
          break;
        }
      }

      // Direct path redaction
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- typeof null === 'object' in JavaScript
      if (current && typeof current === 'object' && current !== null) {
        const currentObj = current;
        const finalKey = pathParts[pathParts.length - 1];
        if (finalKey && finalKey !== '*' && finalKey in currentObj) {
          currentObj[finalKey] = '[REDACTED]';
        }
      }
    }

    return redacted;
  }

  private formatLogEntry(level: LogLevel, message: string, context?: LogContext, error?: Error): LogEntry {
    // Get trace context from OpenTelemetry if available
    const traceContext = getTraceContext ? getTraceContext() : {};

    // Build log entry with standard structure
    const entry: LogEntry = {
      ts: new Date().toISOString(),
      level,
      msg: message,
      service: this.serviceName,
      env: this.environment,
      deployment_environment: this.environment, // Also set for OTel resource correlation
      request_id: this.requestId,
      ...(traceContext.traceId && { trace_id: traceContext.traceId }),
      ...(traceContext.spanId && { span_id: traceContext.spanId }),
    };

    // Add context fields
    if (context) {
      const { userId, orderId, component, action, ...restContext } = context;
      Object.assign(entry, {
        ...(userId && { user_id: userId }),
        ...(orderId && { order_id: orderId }),
        ...(component && { component }),
        ...(action && { action }),
        ...restContext,
      });
    }

    // Add error information if present
    if (error) {
      (entry as Record<string, unknown>)['error'] = {
        name: error.name,
        message: error.message,
        ...(error.stack && { stack: error.stack }),
      };
    }

    // Redact sensitive data
    const redacted = this.redactSensitiveData(entry);

    return redacted as LogEntry;
  }

  private output(entry: LogEntry): void {
    // Output as JSON string (single line for log aggregation)
    const jsonOutput = JSON.stringify(entry);

    // Use appropriate console method based on level
    if (entry.level === 'error') {
      console.error(jsonOutput);
    } else if (entry.level === 'warn') {
      console.warn(jsonOutput);
    } else if (entry.level === 'debug') {
      console.debug(jsonOutput);
    } else {
      console.log(jsonOutput); // @allow-console - This IS the logger implementation
    }

    // Send to Datadog if initialized
    if (isDatadogInitialized()) {
      // Extract context from entry (exclude standard fields)
      const { ts, level, msg, service, env, request_id, trace_id, span_id, ...context } = entry;

      // Map log level to Datadog level
      const datadogLevel = level === 'error' ? 'error' :
                          level === 'warn' ? 'warn' :
                          level === 'debug' ? 'debug' : 'info';

      logToDatadog(datadogLevel, msg, {
        ...context,
        timestamp: ts,
        service,
        env,
        request_id,
        trace_id,
        span_id,
      });
    }
  }

  debug(message: string, context?: LogContext): void {
    if (this.shouldLog('debug')) {
      this.output(this.formatLogEntry('debug', message, context));
    }
  }

  info(message: string, context?: LogContext): void {
    if (this.shouldLog('info')) {
      this.output(this.formatLogEntry('info', message, context));
    }
  }

  warn(message: string, context?: LogContext): void {
    if (this.shouldLog('warn')) {
      this.output(this.formatLogEntry('warn', message, context));
    }
  }

  error(message: string, error?: Error, context?: LogContext): void {
    if (this.shouldLog('error')) {
      this.output(this.formatLogEntry('error', message, context, error));
    }
  }

  // Convenience methods for common operations
  apiCall(method: string, url: string, context?: LogContext): void {
    this.debug(`API call: ${method} ${url}`, {
      action: 'api_call',
      method,
      url,
      ...context,
    });
  }

  apiError(method: string, url: string, error: Error, context?: LogContext): void {
    this.error(`API error: ${method} ${url}`, error, {
      action: 'api_error',
      method,
      url,
      ...context,
    });
  }

  componentError(component: string, error: Error, context?: LogContext): void {
    this.error(`Component error: ${component}`, error, {
      component,
      action: 'component_error',
      ...context,
    });
  }

  userAction(action: string, context?: LogContext): void {
    this.info(`User action: ${action}`, {
      action,
      ...context,
    });
  }
}

// Singleton logger instance
let loggerInstance: Logger | null = null;

/**
 * Get or create the logger instance
 */
export function getLogger(): Logger {
  if (!loggerInstance) {
    loggerInstance = new Logger();
  }
  return loggerInstance;
}

/**
 * Convenience functions for direct use
 */
export const logger = {
  debug: (msg: string, ctx?: LogContext) => getLogger().debug(msg, ctx),
  info: (msg: string, ctx?: LogContext) => getLogger().info(msg, ctx),
  warn: (msg: string, ctx?: LogContext) => getLogger().warn(msg, ctx),
  error: (msg: string, err?: Error, ctx?: LogContext) => getLogger().error(msg, err, ctx),
  apiCall: (method: string, url: string, ctx?: LogContext) => getLogger().apiCall(method, url, ctx),
  apiError: (method: string, url: string, err: Error, ctx?: LogContext) => getLogger().apiError(method, url, err, ctx),
  componentError: (component: string, err: Error, ctx?: LogContext) => getLogger().componentError(component, err, ctx),
  userAction: (action: string, ctx?: LogContext) => getLogger().userAction(action, ctx),
};
